import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Simulate API call (replace with actual forgot password endpoint)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Password reset email sent successfully', {
        description: 'Please check your email for reset instructions.'
      });
      
      // Navigate back to login after success
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Branding */}
        <div className="text-center mb-8 animate-fade-in">
          <img 
            src="https://customer-assets.emergentagent.com/job_proposal-builder-8/artifacts/n1a2dzsn_1.%20cleanifax_logo_without_tagline-2.png"
            alt="Cleanifax Logo"
            className="w-48 h-auto mx-auto mb-4 drop-shadow-lg"
          />
          <p className="text-xl font-bold text-[#247C75]">
            Unified Suite for Operations and CRM
          </p>
        </div>

        {/* Forgot Password Card */}
        <Card className="border-slate-200 shadow-xl animate-slide-up">
          <CardHeader className="space-y-2 text-center pb-6">
            <CardTitle className="text-2xl font-bold text-slate-900">
              Reset Password
            </CardTitle>
            <CardDescription className="text-slate-600">
              Enter your email to receive password reset instructions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 h-12 border-slate-300 focus:border-[#247C75] focus:ring-[#247C75]"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-[#247C75] hover:bg-[#1d6259] text-white font-semibold text-base shadow-md transition-all duration-200"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full h-12 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  onClick={() => navigate('/login')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          © 2025 Cleanifax Inc. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;